import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, ArrowRightLeft, Filter, Settings2, CalendarOff, Trash2, Plus } from 'lucide-react';
import type { DateRange } from 'react-day-picker';
import { eachDayOfInterval, format } from 'date-fns';
import { pl } from 'date-fns/locale';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/sonner';
import { DatePicker, DateRangePicker } from '@/components/ui/date-picker';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  useTemplates,
  useBookings,
  useReassignBooking,
  type BookingRow,
} from '@/lib/booking';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { cn } from '@/lib/cn';

export const Route = createFileRoute('/_app/coordinate')({
  staticData: { crumb: 'Koordynacja' },
  component: CoordinatePage,
});

const SLOT_MIN = 30;
const DAY_START_MIN = 8 * 60; // 08:00
const DAY_END_MIN = 20 * 60; // 20:00
const SLOTS_PER_DAY = (DAY_END_MIN - DAY_START_MIN) / SLOT_MIN;

interface Doctor {
  id: string;
  full_name: string;
}

type BookingConfig = {
  provider?: string;
  url?: string;
  min_lead_minutes?: number;
  default_slot_minutes?: number;
  holidays?: string[];
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

function CoordinatePage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const templates = useTemplates();
  const bookings = useBookings();
  const reassign = useReassignBooking();

  const bookingConfig: BookingConfig = (brief as unknown as { booking?: BookingConfig })?.booking ?? {};
  const holidaySet = React.useMemo(
    () => new Set(bookingConfig.holidays ?? []),
    [bookingConfig.holidays]
  );

  const allDoctors = ((brief?.team ?? []) as Doctor[]) || [];
  const services = React.useMemo(() => {
    const set = new Set<string>();
    for (const t of templates.data ?? []) {
      if (t.service_slugs) for (const s of t.service_slugs) set.add(s);
    }
    return Array.from(set).sort();
  }, [templates.data]);

  const [date, setDate] = React.useState<Date>(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [serviceFilter, setServiceFilter] = React.useState('');
  const [selectedBooking, setSelectedBooking] = React.useState<BookingRow | null>(null);

  const doctors = serviceFilter
    ? allDoctors.filter((d) =>
        (templates.data ?? [])
          .filter((t) => t.doctor_id === d.id)
          .some((t) => !t.service_slugs || t.service_slugs.includes(serviceFilter))
      )
    : allDoctors;

  // Build per-cell data: bookings keyed by `${doctor_id}:${slot_idx}`.
  const dayStartEpoch = Math.floor(date.getTime() / 1000) + DAY_START_MIN * 60;
  const dayEndEpoch = Math.floor(date.getTime() / 1000) + DAY_END_MIN * 60;
  const weekday = ((date.getDay() + 6) % 7) as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  const cellBookings = React.useMemo(() => {
    const map = new Map<string, BookingRow>();
    for (const b of bookings.data ?? []) {
      if (b.start_at < dayStartEpoch || b.start_at >= dayEndEpoch) continue;
      if (b.state === 'cancelled') continue;
      const slotIdx = Math.floor((b.start_at - dayStartEpoch) / (SLOT_MIN * 60));
      if (slotIdx < 0 || slotIdx >= SLOTS_PER_DAY) continue;
      map.set(`${b.doctor_id}:${slotIdx}`, b);
    }
    return map;
  }, [bookings.data, dayStartEpoch, dayEndEpoch]);

  // For each (doctor, slot), is the doctor available?
  const cellAvailable = React.useMemo(() => {
    const map = new Map<string, boolean>();
    for (const t of templates.data ?? []) {
      if (t.weekday !== weekday) continue;
      if (serviceFilter && t.service_slugs && !t.service_slugs.includes(serviceFilter)) continue;
      const tStart = parseHm(t.start_time);
      const tEnd = parseHm(t.end_time);
      for (let m = DAY_START_MIN; m < DAY_END_MIN; m += SLOT_MIN) {
        if (m >= tStart && m + SLOT_MIN <= tEnd) {
          map.set(`${t.doctor_id}:${(m - DAY_START_MIN) / SLOT_MIN}`, true);
        }
      }
    }
    return map;
  }, [templates.data, weekday, serviceFilter]);

  const shiftDay = (delta: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(d);
  };

  const isHoliday = holidaySet.has(ymd(date));
  const [configOpen, setConfigOpen] = React.useState(false);

  const onReassign = async (toDoctorId: string, reason?: string) => {
    if (!selectedBooking) return;
    try {
      await reassign.mutateAsync({
        id: selectedBooking.id,
        new_doctor_id: toDoctorId,
        reason,
      });
      setSelectedBooking(null);
      toast.success('Wizyta przeniesiona.');
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
            Koordynacja lekarzy
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            Wszyscy lekarze obok siebie. Kliknij wizytę, aby ją przenieść do innego lekarza
            (kolizje czasowe są blokowane).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => shiftDay(-1)} aria-label="Poprzedni dzień">
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <div className="min-w-[16rem]">
            <DatePicker
              value={date}
              onChange={(d) => {
                if (!d) return;
                const dd = new Date(d);
                dd.setHours(0, 0, 0, 0);
                setDate(dd);
              }}
            />
          </div>
          <Button variant="ghost" size="icon" onClick={() => shiftDay(1)} aria-label="Następny dzień">
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              const d = new Date();
              d.setHours(0, 0, 0, 0);
              setDate(d);
            }}
          >
            Dziś
          </Button>
          {isHoliday ? (
            <Badge className="bg-warning/15 text-warning ring-1 ring-warning/30">
              <CalendarOff className="size-3" aria-hidden="true" /> Dzień wolny
            </Badge>
          ) : null}
          <Button variant="secondary" size="sm" onClick={() => setConfigOpen(true)}>
            <Settings2 className="size-4" aria-hidden="true" /> Ustawienia rezerwacji
          </Button>
        </div>
      </header>

      <BookingConfigSheet
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={bookingConfig}
        onSave={async (next) => {
          try {
            await update.mutateAsync({
              slice: 'booking',
              data: { ...bookingConfig, ...next },
            });
            toast.success('Zapisano ustawienia rezerwacji.');
          } catch (e) {
            toast.error((e as Error).message);
          }
        }}
      />

      {services.length > 0 ? (
        <div className="flex items-center gap-2">
          <Filter className="size-4 text-text-muted" aria-hidden="true" />
          <span className="text-xs text-text-muted">Usługa:</span>
          <Select
            value={serviceFilter}
            onChange={(e) => setServiceFilter(e.target.value)}
            className="h-9 max-w-xs"
          >
            <option value="">— wszystkie —</option>
            {services.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          {serviceFilter ? (
            <Button variant="ghost" size="sm" onClick={() => setServiceFilter('')}>
              Wyczyść
            </Button>
          ) : null}
        </div>
      ) : null}

      {doctors.length === 0 ? (
        <Card>
          <CardContent>
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak lekarzy. Dodaj zespół w Treści strony → Zespół.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md ring-1 ring-black/5">
          <div className="overflow-x-auto">
            <div
              className="grid min-w-full"
              style={{ gridTemplateColumns: `64px repeat(${doctors.length}, minmax(110px, 1fr))` }}
            >
              <div className="sticky left-0 z-10 bg-surface px-2 py-2 text-xs font-semibold text-text-subtle ring-1 ring-black/5">
                godz.
              </div>
              {doctors.map((d) => (
                <div
                  key={d.id}
                  className="bg-surface px-2 py-2 text-center text-xs font-semibold text-text ring-1 ring-black/5"
                >
                  {d.full_name || d.id}
                </div>
              ))}
              {Array.from({ length: SLOTS_PER_DAY }).map((_, slotIdx) => {
                const min = DAY_START_MIN + slotIdx * SLOT_MIN;
                const hh = String(Math.floor(min / 60)).padStart(2, '0');
                const mm = String(min % 60).padStart(2, '0');
                return (
                  <React.Fragment key={slotIdx}>
                    <div className="sticky left-0 z-10 bg-bg px-2 py-1.5 text-[10px] font-mono text-text-subtle ring-1 ring-black/5">
                      {hh}:{mm}
                    </div>
                    {doctors.map((d) => {
                      const key = `${d.id}:${slotIdx}`;
                      const b = cellBookings.get(key);
                      const available = cellAvailable.get(key);
                      return (
                        <Cell
                          key={key}
                          booking={b}
                          available={available ?? false}
                          onClick={() => b && setSelectedBooking(b)}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {selectedBooking ? (
        <ReassignDrawer
          booking={selectedBooking}
          doctors={allDoctors}
          onClose={() => setSelectedBooking(null)}
          onReassign={onReassign}
          busy={reassign.isPending}
        />
      ) : null}
    </div>
  );
}

function Cell({
  booking,
  available,
  onClick,
}: {
  booking: BookingRow | undefined;
  available: boolean;
  onClick: () => void;
}) {
  if (booking) {
    const stateColor =
      booking.state === 'completed'
        ? 'bg-success/15 ring-success/30 text-success'
        : booking.state === 'no_show'
        ? 'bg-error/10 ring-error/20 text-error'
        : 'bg-accent/10 ring-accent/30 text-accent';
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'cursor-pointer truncate px-1.5 py-1 text-left text-[11px] leading-tight ring-1 ring-inset',
          stateColor,
          'hover:brightness-95'
        )}
        title={`${booking.patient_name} · ${booking.service_slug ?? '—'}`}
      >
        <div className="truncate font-semibold">{booking.patient_name}</div>
        {booking.service_slug ? (
          <div className="truncate text-[9px] opacity-75">{booking.service_slug}</div>
        ) : null}
      </button>
    );
  }
  if (available) {
    return <div className="bg-bg ring-1 ring-inset ring-black/5" />;
  }
  return (
    <div className="bg-surface/40 ring-1 ring-inset ring-black/5 [background-image:repeating-linear-gradient(45deg,transparent_0,transparent_3px,rgba(0,0,0,0.04)_3px,rgba(0,0,0,0.04)_4px)]" />
  );
}

function ReassignDrawer({
  booking,
  doctors,
  onClose,
  onReassign,
  busy,
}: {
  booking: BookingRow;
  doctors: Doctor[];
  onClose: () => void;
  onReassign: (toDoctorId: string, reason?: string) => void;
  busy: boolean;
}) {
  const [target, setTarget] = React.useState('');
  const [reason, setReason] = React.useState('');

  return (
    <Sheet open={true} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Wizyta</SheetTitle>
          <SheetDescription>
            {new Date(booking.start_at * 1000).toLocaleString('pl-PL')} · stan:{' '}
            <span className="font-mono">{booking.state}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="space-y-2 rounded-md bg-surface p-4 ring-1 ring-black/5">
            <p className="text-sm">
              <strong className="font-semibold">{booking.patient_name}</strong>
            </p>
            <p className="text-xs text-text-muted">
              tel.{' '}
              <a href={`tel:${booking.patient_phone}`} className="text-accent hover:underline">
                {booking.patient_phone}
              </a>
            </p>
            {booking.service_slug ? (
              <p className="text-xs text-text-muted">
                usługa: <span className="font-mono">{booking.service_slug}</span>
              </p>
            ) : null}
            <p className="text-xs text-text-muted">
              obecnie u: <span className="font-mono">{booking.doctor_id}</span>
            </p>
            {booking.notes ? (
              <p className="rounded-md bg-bg p-2 text-xs text-text">{booking.notes}</p>
            ) : null}
          </div>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-subtle">
              Przenieś do innego lekarza
            </p>
            <Select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="w-full"
            >
              <option value="">— wybierz —</option>
              {doctors
                .filter((d) => d.id !== booking.doctor_id)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.full_name || d.id}
                  </option>
                ))}
            </Select>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Powód (opcjonalnie)"
              className="mt-2 h-9 w-full rounded-md bg-bg px-3 text-sm ring-1 ring-border focus:outline-2 focus:-outline-offset-2 focus:outline-accent"
            />
            <Button
              type="button"
              className="mt-3 w-full"
              disabled={!target || busy}
              onClick={() => onReassign(target, reason || undefined)}
            >
              <ArrowRightLeft className="size-4" aria-hidden="true" />
              Przenieś wizytę
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function parseHm(s: string): number {
  const [h, m] = s.split(':').map((x) => parseInt(x, 10));
  return (h ?? 0) * 60 + (m ?? 0);
}

function BookingConfigSheet({
  open,
  onClose,
  config,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  config: BookingConfig;
  onSave: (next: Partial<BookingConfig>) => Promise<void>;
}) {
  const [minLead, setMinLead] = React.useState<number>(config.min_lead_minutes ?? 120);
  const [defaultSlot, setDefaultSlot] = React.useState<number>(config.default_slot_minutes ?? 30);
  const [holidays, setHolidays] = React.useState<string[]>(config.holidays ?? []);
  const [range, setRange] = React.useState<DateRange | undefined>(undefined);

  React.useEffect(() => {
    setMinLead(config.min_lead_minutes ?? 120);
    setDefaultSlot(config.default_slot_minutes ?? 30);
    setHolidays(config.holidays ?? []);
  }, [config]);

  const addRange = () => {
    if (!range?.from) return;
    const days = range.to
      ? eachDayOfInterval({ start: range.from, end: range.to })
      : [range.from];
    const ymds = days.map((d) => ymd(d));
    setHolidays((prev) => Array.from(new Set([...prev, ...ymds])).sort());
    setRange(undefined);
  };

  const removeDay = (s: string) => {
    setHolidays((prev) => prev.filter((d) => d !== s));
  };

  const persist = async () => {
    await onSave({
      min_lead_minutes: minLead,
      default_slot_minutes: defaultSlot,
      holidays,
    });
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full max-w-md flex-col gap-5 sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Ustawienia rezerwacji</SheetTitle>
          <SheetDescription>
            Wpływają na publiczny formularz rezerwacji oraz dostępność slotów w kalendarzu.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 overflow-y-auto pr-1 scrollbar-thin">
          <Field
            label="Minimalny czas przed wizytą"
            htmlFor="min-lead"
            hint="Ile czasu pacjent musi zostawić między rezerwacją a wizytą. Domyślnie 2h."
          >
            <div className="flex items-center gap-2">
              <Input
                id="min-lead"
                type="number"
                min={0}
                max={60 * 24 * 14}
                step={30}
                value={minLead}
                onChange={(e) => setMinLead(Number(e.target.value) || 0)}
                className="w-32 tabular-nums"
              />
              <span className="text-xs text-text-muted">min ({Math.round(minLead / 60 * 10) / 10} h)</span>
            </div>
          </Field>

          <Field
            label="Domyślna długość slotu"
            htmlFor="slot-min"
            hint="Każda dostępność lekarza może to nadpisać per-szablon."
          >
            <div className="flex items-center gap-2">
              <Input
                id="slot-min"
                type="number"
                min={5}
                max={240}
                step={5}
                value={defaultSlot}
                onChange={(e) => setDefaultSlot(Number(e.target.value) || 30)}
                className="w-32 tabular-nums"
              />
              <span className="text-xs text-text-muted">min</span>
            </div>
          </Field>

          <div>
            <p className="mb-1 text-sm font-medium text-text">Dni wolne / blackouts</p>
            <p className="mb-2 text-xs text-text-muted">
              Pełne dni bez slotów (święta, urlop kliniki). Pacjent zobaczy „dzień wolny" zamiast godzin.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <div className="min-w-[14rem] flex-1">
                <DateRangePicker
                  value={range}
                  onChange={setRange}
                  placeholder="Wybierz dzień lub zakres dat"
                />
              </div>
              <Button type="button" size="sm" disabled={!range?.from} onClick={addRange}>
                <Plus className="size-3.5" aria-hidden="true" /> Dodaj
              </Button>
            </div>
            {holidays.length > 0 ? (
              <ul role="list" className="mt-3 max-h-48 space-y-1 overflow-y-auto pr-1 scrollbar-thin">
                {holidays.map((s) => (
                  <li
                    key={s}
                    className="flex items-center justify-between rounded-md bg-surface px-2 py-1 text-xs ring-1 ring-black/5"
                  >
                    <span className="font-mono tabular-nums">{format(fromYmd(s), 'EEE, d MMM yyyy', { locale: pl })}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeDay(s)}
                      aria-label="Usuń"
                    >
                      <Trash2 className="size-3.5" aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 rounded-md bg-surface p-2 text-xs text-text-muted ring-1 ring-black/5">
                Brak dni wolnych.
              </p>
            )}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="button" onClick={persist}>
            Zapisz
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
